import type { ScopeSectionSchema } from './types'
import { IDV_VENDORS } from '../types'

export const biometricsSections: ScopeSectionSchema[] = [
  {
    id: 'biometrics_core',
    tabId: 'biometrics',
    formTitle: 'Biometrics & IDV',
    formDescription:
      'Biometrics & IDV combines document verification with facial similarity checks to confirm that the person onboarding is the legitimate document holder. This adds a critical layer of fraud prevention to your verification process.',
    pdfTitle: 'ID Verification',
    pdfDescription: 'Document verification and biometrics configuration.',
    highLevelSummary: 'ID verification enablement, vendor, and experience configuration.',
    groups: [
      {
        id: 'biometrics_tab_header',
        tabId: 'biometrics',
        sectionId: 'biometrics_core',
        formTitle: 'Biometrics & IDV',
        formDescription:
          'Biometrics & IDV combines document verification with facial similarity checks to confirm that the person onboarding is the legitimate document holder. This adds a critical layer of fraud prevention to your verification process.',
        tooltip:
          "Configure document scanning and facial verification to verify your customers' identities.",
        pdfTitle: 'ID Verification',
        pdfDescription: 'Document verification and biometrics configuration.',
      },
    ],
    fields: [],
  },
  {
    id: 'biometrics_enablement',
    tabId: 'biometrics',
    formTitle: 'Enablement',
    formDescription: 'Does this experience need document and/or facial verification capabilities?',
    pdfTitle: 'Enablement',
    pdfDescription: 'Whether ID verification is enabled.',
    highLevelSummary: 'Enablement decision for ID verification.',
    groups: [
      {
        id: 'biometrics_enablement_group',
        tabId: 'biometrics',
        sectionId: 'biometrics_enablement',
        formTitle: 'Enablement',
        tooltip: 'Does this experience need document and/or facial verification capabilities?',
        pdfTitle: 'Enablement',
        pdfDescription: 'Whether ID verification is enabled.',
      },
    ],
    fields: [
      {
        id: 'biometrics_enabled',
        tabId: 'biometrics',
        sectionId: 'biometrics_enablement',
        groupId: 'biometrics_enablement_group',
        path: 'biometricsConfig.enabled',
        valueType: 'tri_state',
        formLabel: 'Enable Biometrics & IDV',
        formDescription: 'Enable document verification and/or facial similarity checks.',
        pdfLabel: 'Enabled',
        pdfDescription: 'Whether ID verification is enabled.',
        contributesToCompletion: true,
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No (Not needed)' },
          { value: 'unknown', label: "Don't know" },
        ],
      },
    ],
  },
  {
    id: 'biometrics_vendor',
    tabId: 'biometrics',
    formTitle: 'Vendor',
    formDescription:
      'The IDV vendor performs document authenticity checks, OCR extraction, liveness detection, and face comparison. FrankieOne integrates with multiple vendors to provide flexibility without vendor lock-in.',
    pdfTitle: 'Vendor',
    pdfDescription: 'IDV vendor selection.',
    highLevelSummary: 'IDV vendor selection.',
    groups: [
      {
        id: 'biometrics_vendor_group',
        tabId: 'biometrics',
        sectionId: 'biometrics_vendor',
        formTitle: 'Vendor',
        tooltip:
          'Select the identity verification provider that will process document and biometric checks.',
        helperText: 'Required for completion.',
        pdfTitle: 'Vendor',
        pdfDescription: 'IDV vendor selection.',
        visibleWhen: [{ dependsOn: 'biometricsConfig.enabled', equals: 'yes' }],
      },
    ],
    fields: [
      {
        id: 'biometrics_vendor_primary',
        tabId: 'biometrics',
        sectionId: 'biometrics_vendor',
        groupId: 'biometrics_vendor_group',
        path: 'biometricsConfig.vendor',
        valueType: 'select',
        formLabel: 'Primary Vendor',
        formDescription: 'Select the primary vendor for ID verification.',
        pdfLabel: 'Primary vendor',
        pdfDescription: 'Select the ID verification vendor (or choose Other and specify).',
        contributesToCompletion: true,
        options: [
          { value: 'unknown', label: "Don't know" },
          { value: '', label: '—' },
          ...IDV_VENDORS.map((v) => ({ value: v.id, label: v.label })),
        ],
        visibleWhen: [{ dependsOn: 'biometricsConfig.enabled', equals: 'yes' }],
      },
      {
        id: 'biometrics_vendor_other',
        tabId: 'biometrics',
        sectionId: 'biometrics_vendor',
        groupId: 'biometrics_vendor_group',
        path: 'biometricsConfig.otherVendor',
        valueType: 'text',
        formLabel: 'Other Vendor Name',
        formDescription: 'If “Other” is selected above, specify the vendor name.',
        placeholder: 'Enter vendor name',
        pdfLabel: 'Other vendor',
        pdfDescription: 'Other vendor name (when applicable).',
        contributesToCompletion: false,
        visibleWhen: [
          { dependsOn: 'biometricsConfig.enabled', equals: 'yes' },
          { dependsOn: 'biometricsConfig.vendor', equals: 'other' },
        ],
      },
      {
        id: 'biometrics_vendor_note',
        tabId: 'biometrics',
        sectionId: 'biometrics_vendor',
        groupId: 'biometrics_vendor_group',
        path: 'biometricsConfig.vendorNote',
        valueType: 'textarea',
        formLabel: 'Vendor Details',
        formDescription: 'Capture additional details about the vendor integration, configuration requirements, or special considerations.',
        placeholder: 'e.g., Direct integration details, API requirements, special configuration needed...',
        pdfLabel: 'Vendor details',
        pdfDescription: 'Additional vendor integration details and requirements.',
        contributesToCompletion: false,
        visibleWhen: [
          { dependsOn: 'biometricsConfig.enabled', equals: 'yes' },
          { dependsOn: 'biometricsConfig.vendor', notEquals: 'frankieone' },
          { dependsOn: 'biometricsConfig.vendor', notEquals: 'unknown' },
        ],
      },
    ],
  },
  {
    id: 'biometrics_flows',
    tabId: 'biometrics',
    formTitle: 'Flows',
    formDescription:
      'Flows are complete verification experiences you send to customers. Each flow consists of screens shown in sequence. You can create up to 5 different flows and choose which one to send to each customer via API based on their risk profile or customer segment.',
    pdfTitle: 'Flows',
    pdfDescription: 'IDV flow configurations.',
    highLevelSummary: 'IDV flow definitions (dynamic).',
    groups: [
      {
        id: 'biometrics_flows_group',
        tabId: 'biometrics',
        sectionId: 'biometrics_flows',
        formTitle: 'Flows',
        tooltip:
          'Create verification journeys with different screens and settings for different customer types.',
        pdfTitle: 'Flows',
        pdfDescription:
          'Flows are complete verification experiences you send to customers. Each flow consists of screens shown in sequence. You can create up to 5 different flows and choose which one to send to each customer via API based on their risk profile or customer segment.',
        visibleWhen: [{ dependsOn: 'biometricsConfig.enabled', equals: 'yes' }],
      },
    ],
    fields: [
      {
        id: 'biometrics_flows_summary',
        tabId: 'biometrics',
        sectionId: 'biometrics_flows',
        groupId: 'biometrics_flows_group',
        path: 'biometricsConfig.flows',
        valueType: 'json',
        formLabel: 'Flows',
        formDescription: 'Add one or more flows and select required screens/providers.',
        pdfLabel: 'Flows',
        pdfDescription: 'Add one or more flows and select required screens/providers.',
        contributesToCompletion: false,
        visibleWhen: [{ dependsOn: 'biometricsConfig.enabled', equals: 'yes' }],
      },
      {
        id: 'biometrics_flow_consent_screen',
        tabId: 'biometrics',
        sectionId: 'biometrics_flows',
        groupId: 'biometrics_flows_group',
        path: 'biometricsConfig.flows[].consentScreenEnabled',
        valueType: 'tri_state',
        formLabel: 'Consent Screen',
        formDescription: 'Displays terms and conditions that customers must accept before starting verification.',
        tooltip: 'The consent screen shows your terms of service, privacy policy, and data collection notices. Customers must explicitly agree before proceeding with identity verification.',
        pdfLabel: 'Consent screen',
        pdfDescription: 'Whether flows include a consent screen.',
        contributesToCompletion: false,
        visibleWhen: [{ dependsOn: 'biometricsConfig.enabled', equals: 'yes' }],
      },
      {
        id: 'biometrics_flow_id_form',
        tabId: 'biometrics',
        sectionId: 'biometrics_flows',
        groupId: 'biometrics_flows_group',
        path: 'biometricsConfig.flows[].idFormEnabled',
        valueType: 'tri_state',
        formLabel: 'ID Form',
        formDescription: 'Manual identity data entry form before document capture.',
        tooltip: 'The ID Form allows customers to manually enter their identity details (name, date of birth, address, etc.) before proceeding to document capture. This pre-fills verification data and can improve match accuracy.',
        pdfLabel: 'ID form',
        pdfDescription: 'Whether flows include an ID data entry form.',
        contributesToCompletion: false,
        visibleWhen: [{ dependsOn: 'biometricsConfig.enabled', equals: 'yes' }],
      },
      {
        id: 'biometrics_flow_id_verification',
        tabId: 'biometrics',
        sectionId: 'biometrics_flows',
        groupId: 'biometrics_flows_group',
        path: 'biometricsConfig.flows[].idVerificationEnabled',
        valueType: 'tri_state',
        formLabel: 'ID Verification',
        formDescription: 'Document capture and authenticity verification.',
        tooltip: 'ID Verification captures and validates government-issued documents (passport, driver license, etc.). The system checks document authenticity, extracts data via OCR, and verifies security features.',
        pdfLabel: 'ID verification',
        pdfDescription: 'Whether flows include ID document capture and verification.',
        contributesToCompletion: false,
        visibleWhen: [{ dependsOn: 'biometricsConfig.enabled', equals: 'yes' }],
      },
      {
        id: 'biometrics_flow_double_id',
        tabId: 'biometrics',
        sectionId: 'biometrics_flows',
        groupId: 'biometrics_flows_group',
        path: 'biometricsConfig.flows[].doubleIdEnabled',
        valueType: 'tri_state',
        formLabel: 'Double ID Flow',
        formDescription: 'Requires two identity documents for higher assurance verification (Daon only).',
        tooltip: 'When enabled, the customer must capture and submit two separate identity documents (e.g., passport + driver licence) in a single verification flow. This provides higher identity assurance by cross-referencing data from multiple documents. Only available for Daon vendor flows.',
        pdfLabel: 'Double ID flow',
        pdfDescription: 'Whether Daon flows require double ID document capture.',
        contributesToCompletion: false,
        visibleWhen: [{ dependsOn: 'biometricsConfig.enabled', equals: 'yes' }],
      },
      {
        id: 'biometrics_flow_biometrics',
        tabId: 'biometrics',
        sectionId: 'biometrics_flows',
        groupId: 'biometrics_flows_group',
        path: 'biometricsConfig.flows[].biometricsEnabled',
        valueType: 'tri_state',
        formLabel: 'Biometrics',
        formDescription: 'Selfie capture with liveness detection and face matching.',
        tooltip: 'Biometrics captures a selfie with liveness detection to prevent photo/video spoofing, then compares the live selfie to the document photo to confirm the person presenting the ID is the legitimate document holder.',
        pdfLabel: 'Biometrics (facial similarity)',
        pdfDescription: 'Whether flows include selfie capture and facial similarity matching.',
        contributesToCompletion: false,
        visibleWhen: [{ dependsOn: 'biometricsConfig.enabled', equals: 'yes' }],
      },
      {
        id: 'biometrics_flow_review_screen',
        tabId: 'biometrics',
        sectionId: 'biometrics_flows',
        groupId: 'biometrics_flows_group',
        path: 'biometricsConfig.flows[].reviewScreenEnabled',
        valueType: 'tri_state',
        formLabel: 'Review Screen',
        formDescription: 'Allows customers to review captured data before final submission.',
        tooltip: 'The review screen displays all captured information (document images, extracted data, selfie) for customer review. Customers can confirm accuracy or go back to retake photos before final submission.',
        pdfLabel: 'Review screen',
        pdfDescription: 'Whether flows include a review screen for customers to confirm captured data.',
        contributesToCompletion: false,
        visibleWhen: [{ dependsOn: 'biometricsConfig.enabled', equals: 'yes' }],
      },
      {
        id: 'biometrics_flow_redirect_page',
        tabId: 'biometrics',
        sectionId: 'biometrics_flows',
        groupId: 'biometrics_flows_group',
        path: 'biometricsConfig.flows[].redirectPageEnabled',
        valueType: 'tri_state',
        formLabel: 'Redirect Page',
        formDescription: 'Custom completion page shown after verification finishes.',
        tooltip: 'The redirect page is shown after verification completes. You can customize this page with your own messaging, branding, and next steps, or redirect customers back to your application.',
        pdfLabel: 'Redirect page',
        pdfDescription: 'Whether flows include a custom redirect/completion page.',
        contributesToCompletion: false,
        visibleWhen: [{ dependsOn: 'biometricsConfig.enabled', equals: 'yes' }],
      },
      {
        id: 'biometrics_flow_custom_screen',
        tabId: 'biometrics',
        sectionId: 'biometrics_flows',
        groupId: 'biometrics_flows_group',
        path: 'biometricsConfig.flows[].customScreenEnabled',
        valueType: 'tri_state',
        formLabel: 'Custom Screen',
        formDescription: 'Add a custom screen to this flow with specific requirements.',
        tooltip: 'Enable a custom screen and describe its purpose and content in the details field.',
        pdfLabel: 'Custom screen',
        pdfDescription: 'Whether flows include a custom screen.',
        contributesToCompletion: false,
        visibleWhen: [{ dependsOn: 'biometricsConfig.enabled', equals: 'yes' }],
      },
      {
        id: 'biometrics_flow_custom_screen_details',
        tabId: 'biometrics',
        sectionId: 'biometrics_flows',
        groupId: 'biometrics_flows_group',
        path: 'biometricsConfig.flows[].customScreenDetails',
        valueType: 'text',
        formLabel: 'Custom Screen Details',
        formDescription: 'Describe the custom screen purpose and content.',
        placeholder: 'Describe the custom screen...',
        pdfLabel: 'Custom screen details',
        pdfDescription: 'Details of the custom screen.',
        contributesToCompletion: false,
        visibleWhen: [
          { dependsOn: 'biometricsConfig.enabled', equals: 'yes' },
          { dependsOn: 'biometricsConfig.flows[].customScreenEnabled', equals: 'yes' },
        ],
      },
      {
        id: 'biometrics_flow_name',
        tabId: 'biometrics',
        sectionId: 'biometrics_flows',
        groupId: 'biometrics_flows_group',
        path: 'biometricsConfig.flows[].name',
        valueType: 'text',
        formLabel: 'Flow Name',
        formDescription: 'Internal name to identify this flow.',
        tooltip: 'Internal name to identify this flow (not shown to customers). Used for reference in configuration and reporting.',
        placeholder: 'e.g. Standard IDV Flow',
        pdfLabel: 'Flow name',
        pdfDescription: 'Flow name (internal identifier).',
        contributesToCompletion: false,
        visibleWhen: [{ dependsOn: 'biometricsConfig.enabled', equals: 'yes' }],
      },
      {
        id: 'biometrics_flow_id',
        tabId: 'biometrics',
        sectionId: 'biometrics_flows',
        groupId: 'biometrics_flows_group',
        path: 'biometricsConfig.flows[].id',
        valueType: 'text',
        formLabel: 'Flow ID',
        formDescription: 'Unique identifier for API integration.',
        tooltip: 'Unique identifier used when referencing this flow in recipes (workflows) and API calls. Use lowercase with underscores (e.g., standard_flow, high_risk_flow).',
        placeholder: 'e.g. idv, manual_kyc, high_risk_flow',
        pdfLabel: 'Flow ID',
        pdfDescription: 'Flow ID (API identifier).',
        contributesToCompletion: false,
        visibleWhen: [{ dependsOn: 'biometricsConfig.enabled', equals: 'yes' }],
      },
      {
        id: 'biometrics_flow_vendor',
        tabId: 'biometrics',
        sectionId: 'biometrics_flows',
        groupId: 'biometrics_flows_group',
        path: 'biometricsConfig.flows[].vendor',
        valueType: 'select',
        formLabel: 'Vendor',
        formDescription: 'IDV vendor for this flow.',
        tooltip: 'Which IDV vendor will process verifications for this specific flow? Different flows can use different vendors to support multi-vendor strategies.',
        pdfLabel: 'Flow vendor',
        pdfDescription: 'IDV vendor for this flow.',
        contributesToCompletion: false,
        options: [
          { value: 'unknown', label: "Don't know" },
          { value: 'no_vendor', label: 'No Vendor' },
          { value: 'incode', label: 'Incode' },
          { value: 'daon', label: 'Daon' },
        ],
        visibleWhen: [{ dependsOn: 'biometricsConfig.enabled', equals: 'yes' }],
      },
    ],
  },
  {
    id: 'biometrics_service_level',
    tabId: 'biometrics',
    formTitle: 'Service level',
    formDescription: 'Service level for ID verification (document vs biometrics vs review).',
    pdfTitle: 'Service level',
    pdfDescription: 'ID verification service level.',
    highLevelSummary: 'IDV service level selection.',
    groups: [],
    fields: [
      {
        id: 'biometrics_service_level',
        tabId: 'biometrics',
        sectionId: 'biometrics_service_level',
        path: 'biometricsConfig.serviceLevel',
        valueType: 'select',
        formLabel: 'Service level',
        formDescription: 'Select the required service level (document+review or document+biometrics+review).',
        pdfLabel: 'Service level',
        pdfDescription: 'Select the required service level (document+review or document+biometrics+review).',
        contributesToCompletion: true,
        options: [
          { value: 'unknown', label: "Don't know" },
          { value: 'document_review', label: 'Document + Review' },
          { value: 'document_biometrics_review', label: 'Document + Biometrics + Review' },
        ],
        visibleWhen: [{ dependsOn: 'biometricsConfig.enabled', equals: 'yes' }],
      },
    ],
  },
  {
    id: 'biometrics_document_types',
    tabId: 'biometrics',
    formTitle: 'Supported document types',
    formDescription: 'Document types to capture in ID verification flows.',
    pdfTitle: 'Supported document types',
    pdfDescription: 'Document types supported in ID verification.',
    highLevelSummary: 'IDV supported document types.',
    groups: [],
    fields: [
      {
        id: 'biometrics_document_types',
        tabId: 'biometrics',
        sectionId: 'biometrics_document_types',
        path: 'biometricsConfig.documentTypes',
        valueType: 'multi_select',
        formLabel: 'Supported document types',
        formDescription: 'Select the document types that should be captured in the ID verification flow(s).',
        pdfLabel: 'Supported document types',
        pdfDescription: 'Select the document types that should be captured in the ID verification flow(s).',
        contributesToCompletion: true,
        visibleWhen: [{ dependsOn: 'biometricsConfig.enabled', equals: 'yes' }],
      },
    ],
  },
  {
    id: 'biometrics_frontend',
    tabId: 'biometrics',
    formTitle: 'Frontend Implementation',
    formDescription:
      'Choose how the verification interface will be delivered to your customers. This determines the technical integration approach and level of customization available.',
    pdfTitle: 'Frontend',
    pdfDescription: 'Frontend integration approach.',
    highLevelSummary: 'Frontend integration approach for ID verification.',
    groups: [
      {
        id: 'biometrics_frontend_group',
        tabId: 'biometrics',
        sectionId: 'biometrics_frontend',
        formTitle: 'Frontend Implementation',
        tooltip: 'How will customers access the verification experience?',
        pdfTitle: 'Frontend implementation',
        pdfDescription: 'Select how the verification UI will be delivered (hosted OneSDK vs embedded).',
        visibleWhen: [{ dependsOn: 'biometricsConfig.enabled', equals: 'yes' }],
      },
    ],
    fields: [
      {
        id: 'biometrics_frontend_choice',
        tabId: 'biometrics',
        sectionId: 'biometrics_frontend',
        path: 'biometricsConfig.frontend',
        valueType: 'select',
        formLabel: 'Frontend',
        formDescription: 'How will customers access the verification experience?',
        pdfLabel: 'Frontend',
        pdfDescription: 'Frontend integration approach.',
        contributesToCompletion: true,
        options: [
          { value: 'hosted_onesdk', label: 'HostedOne SDK' },
          { value: 'embedded_onesdk', label: 'Embedded OneSDK' },
          { value: 'none', label: 'None' },
          { value: 'other', label: 'Other' },
          { value: 'unknown', label: "Don't know" },
        ],
        visibleWhen: [{ dependsOn: 'biometricsConfig.enabled', equals: 'yes' }],
      },
      {
        id: 'biometrics_frontend_other',
        tabId: 'biometrics',
        sectionId: 'biometrics_frontend',
        path: 'biometricsConfig.otherFrontend',
        valueType: 'text',
        formLabel: 'Describe Frontend Solution',
        formDescription: 'If “Other” is selected above, describe the frontend solution.',
        placeholder: 'Enter frontend solution details',
        pdfLabel: 'Other frontend solution',
        pdfDescription: 'Other frontend solution details.',
        contributesToCompletion: false,
        visibleWhen: [
          { dependsOn: 'biometricsConfig.enabled', equals: 'yes' },
          { dependsOn: 'biometricsConfig.frontend', equals: 'other' },
        ],
      },
    ],
  },
  {
    id: 'biometrics_sms',
    tabId: 'biometrics',
    formTitle: 'Custom SMS message',
    formDescription:
      "Personalize the message customers receive when you send verification links via SMS. Use {{customer_name}} to automatically insert the customer's name.",
    pdfTitle: 'SMS message',
    pdfDescription: 'SMS template and link validity.',
    highLevelSummary: 'SMS template configuration and link validity.',
    groups: [
      {
        id: 'biometrics_sms_group',
        tabId: 'biometrics',
        sectionId: 'biometrics_sms',
        formTitle: 'Custom SMS message',
        tooltip: 'Customize the SMS message sent to customers with their verification link.',
        pdfTitle: 'Custom SMS message',
        pdfDescription: 'SMS template and link validity.',
        visibleWhen: [{ dependsOn: 'biometricsConfig.enabled', equals: 'yes' }],
      },
      {
        id: 'biometrics_sms_validity_group',
        tabId: 'biometrics',
        sectionId: 'biometrics_sms',
        formTitle: 'Onboarding link validity (hours)',
        helperText: 'Optional: how long the SMS onboarding link remains valid.',
        pdfTitle: 'SMS link validity',
        pdfDescription: 'SMS onboarding link validity in hours.',
        visibleWhen: [{ dependsOn: 'biometricsConfig.enabled', equals: 'yes' }],
      },
    ],
    fields: [
      {
        id: 'biometrics_sms_settings_confirmed',
        tabId: 'biometrics',
        sectionId: 'biometrics_sms',
        path: 'biometricsConfig.smsSettingsConfirmed',
        valueType: 'tri_state',
        formLabel: 'SMS settings confirmed',
        formDescription: 'Whether the customer has explicitly confirmed the SMS template and link validity settings.',
        pdfLabel: 'SMS settings confirmed',
        pdfDescription: 'Confirm whether SMS template and link validity have been explicitly agreed.',
        contributesToCompletion: false,
        visibleWhen: [{ dependsOn: 'biometricsConfig.enabled', equals: 'yes' }],
      },
      {
        id: 'biometrics_sms_template',
        tabId: 'biometrics',
        sectionId: 'biometrics_sms',
        path: 'biometricsConfig.customSmsMessageTemplate',
        valueType: 'textarea',
        formLabel: 'Custom SMS message',
        formDescription:
          "Personalize the message customers receive when you send verification links via SMS. Supported placeholders: {{customer_name}}, {{onboarding_url}}, {{link_validity_hours}}.",
        placeholder: 'Enter the SMS message template...',
        pdfLabel: 'SMS template',
        pdfDescription: 'Confirm the SMS link message content (optional but should be explicitly agreed). Supported placeholders: {{customer_name}}, {{onboarding_url}}, {{link_validity_hours}}.',
        contributesToCompletion: false,
        visibleWhen: [{ dependsOn: 'biometricsConfig.enabled', equals: 'yes' }],
      },
      {
        id: 'biometrics_sms_link_validity_hours',
        tabId: 'biometrics',
        sectionId: 'biometrics_sms',
        path: 'biometricsConfig.smsLinkValidityHours',
        valueType: 'number',
        formLabel: 'Onboarding link validity (hours)',
        formDescription: 'How long the SMS onboarding link remains valid.',
        placeholder: 'e.g. 24',
        pdfLabel: 'SMS link validity (hours)',
        pdfDescription: 'Optional: how long the SMS onboarding link remains valid.',
        contributesToCompletion: false,
        visibleWhen: [{ dependsOn: 'biometricsConfig.enabled', equals: 'yes' }],
      },
    ],
  },
  {
    id: 'biometrics_platform',
    tabId: 'advanced',
    formTitle: 'Platform',
    formDescription: 'Target platform for the verification experience.',
    pdfTitle: 'Platform',
    pdfDescription: 'Web vs in-app.',
    highLevelSummary: 'Platform selection for verification experience.',
    groups: [
      {
        id: 'biometrics_platform_group',
        tabId: 'advanced',
        sectionId: 'biometrics_platform',
        formTitle: 'Platform',
        formDescription: 'Select whether customers will complete verification on web browsers, within your mobile app, or both.',
        tooltip: 'Platform determines the technical integration approach and available features. Web-based uses browser access, while in-app SDK embeds verification directly into your mobile application.',
        pdfTitle: 'Platform',
        pdfDescription: 'Web vs in-app.',
        visibleWhen: [{ dependsOn: 'biometricsConfig.enabled', equals: 'yes' }],
      },
    ],
    fields: [
      {
        id: 'biometrics_platform_choice',
        tabId: 'advanced',
        sectionId: 'biometrics_platform',
        path: 'biometricsConfig.platform',
        valueType: 'select',
        formLabel: 'Platform',
        formDescription: 'Select whether customers will complete verification on web browsers, within your mobile app, or both.',
        tooltip: 'Platform determines the technical integration approach and available features. Web-based uses browser access, while in-app SDK embeds verification directly into your mobile application.',
        pdfLabel: 'Platform',
        pdfDescription: 'Web vs in-app.',
        contributesToCompletion: true,
        options: [
          { value: 'web', label: 'Web-based' },
          { value: 'in_app', label: 'In-App (SDK)' },
          { value: 'both', label: 'Both' },
          { value: 'unknown', label: "Don't know" },
        ],
        visibleWhen: [{ dependsOn: 'biometricsConfig.enabled', equals: 'yes' }],
      },
    ],
  },
  {
    id: 'biometrics_branding',
    tabId: 'biometrics',
    formTitle: 'Branding Requirements',
    formDescription:
      "Branding options allow you to customize the verification experience to match your client's brand identity. Select all customizations that apply.",
    pdfTitle: 'Branding',
    pdfDescription: 'Branding requirements.',
    highLevelSummary: 'Branding requirements and notes.',
    groups: [
      {
        id: 'biometrics_branding_group',
        tabId: 'biometrics',
        sectionId: 'biometrics_branding',
        formTitle: 'Branding Requirements',
        tooltip: 'Select which branding customizations you need for this client.',
        pdfTitle: 'Branding requirements',
        pdfDescription: 'Branding requirements.',
        visibleWhen: [{ dependsOn: 'biometricsConfig.enabled', equals: 'yes' }],
      },
      {
        id: 'biometrics_branding_notes_group',
        tabId: 'biometrics',
        sectionId: 'biometrics_branding',
        formTitle: 'Branding Notes',
        tooltip: 'Specify brand asset locations, special requirements, or design preferences.',
        pdfTitle: 'Branding notes',
        pdfDescription: 'Branding notes.',
        visibleWhen: [{ dependsOn: 'biometricsConfig.enabled', equals: 'yes' }],
      },
    ],
    fields: [
      {
        id: 'biometrics_rebranding_hosted_onesdk',
        tabId: 'biometrics',
        sectionId: 'biometrics_branding',
        path: 'biometricsConfig.rebrandingHostedOneSDK',
        valueType: 'tri_state',
        formLabel: 'Rebranding HostedOneSDK',
        formDescription: 'Custom branding on hosted solution',
        pdfLabel: 'Rebranding HostedOneSDK',
        pdfDescription: 'Custom branding on hosted solution.',
        contributesToCompletion: false,
        visibleWhen: [{ dependsOn: 'biometricsConfig.enabled', equals: 'yes' }],
      },
      {
        id: 'biometrics_rebranding_daon',
        tabId: 'biometrics',
        sectionId: 'biometrics_branding',
        path: 'biometricsConfig.rebrandingDaon',
        valueType: 'tri_state',
        formLabel: 'Rebranding Daon',
        formDescription: 'Custom branding on Daon integration',
        pdfLabel: 'Rebranding Daon',
        pdfDescription: 'Custom branding on Daon integration.',
        contributesToCompletion: false,
        visibleWhen: [{ dependsOn: 'biometricsConfig.enabled', equals: 'yes' }],
      },
      {
        id: 'biometrics_extended_branding',
        tabId: 'biometrics',
        sectionId: 'biometrics_branding',
        path: 'biometricsConfig.extendedBranding',
        valueType: 'tri_state',
        formLabel: 'Extended Branding',
        formDescription: 'Custom pages, colors, logos beyond standard options',
        pdfLabel: 'Extended branding',
        pdfDescription: 'Extended branding requirements.',
        contributesToCompletion: false,
        visibleWhen: [{ dependsOn: 'biometricsConfig.enabled', equals: 'yes' }],
      },
      {
        id: 'biometrics_custom_redirect_page',
        tabId: 'biometrics',
        sectionId: 'biometrics_branding',
        path: 'biometricsConfig.customRedirectPage',
        valueType: 'tri_state',
        formLabel: 'Custom Redirect Page',
        formDescription: 'Custom page after verification completion',
        pdfLabel: 'Custom redirect page',
        pdfDescription: 'Custom redirect page requirement.',
        contributesToCompletion: false,
        visibleWhen: [{ dependsOn: 'biometricsConfig.enabled', equals: 'yes' }],
      },
      {
        id: 'biometrics_custom_consents',
        tabId: 'biometrics',
        sectionId: 'biometrics_branding',
        path: 'biometricsConfig.customConsents',
        valueType: 'tri_state',
        formLabel: 'Custom Consents',
        formDescription: 'Customer adding their own consent forms',
        pdfLabel: 'Custom consents',
        pdfDescription: 'Custom consents requirement.',
        contributesToCompletion: false,
        visibleWhen: [{ dependsOn: 'biometricsConfig.enabled', equals: 'yes' }],
      },
      {
        id: 'biometrics_branding_notes',
        tabId: 'biometrics',
        sectionId: 'biometrics_branding',
        path: 'biometricsConfig.brandingNotes',
        valueType: 'textarea',
        formLabel: 'Branding Notes',
        formDescription: 'Additional branding requirements or notes.',
        placeholder: 'Enter any additional branding requirements or notes...',
        pdfLabel: 'Branding notes',
        pdfDescription: 'Additional branding requirements or notes.',
        contributesToCompletion: false,
        visibleWhen: [{ dependsOn: 'biometricsConfig.enabled', equals: 'yes' }],
      },
    ],
  },
]
